import { useState, useEffect, useCallback } from 'react';
import { toast } from 'sonner';

export interface DashboardCard {
  id: string;
  component: string;
  title: string;
  order: number;
  visible: boolean;
  size?: 'small' | 'medium' | 'large' | 'wide' | 'tall' | 'extra-wide';
  colSpan?: number;
  rowSpan?: number;
}

// Versão da configuração - incrementar quando houver mudanças importantes
const CONFIG_VERSION = '1.3.0';

// Perfis predefinidos de configuração
export const DASHBOARD_PRESETS = {
  minimal: {
    name: 'Minimalista',
    description: 'Apenas métricas essenciais',
    cards: [
      { id: 'response-times', visible: true, size: 'medium', order: 0 },
      { id: 'priority-distribution', visible: true, size: 'medium', order: 1 },
      { id: 'recent-tickets', visible: true, size: 'large', order: 2 },
    ]
  },
  complete: {
    name: 'Completo',
    description: 'Todas as métricas disponíveis',
    cards: [
      { id: 'response-times', visible: true, size: 'medium', order: 0 },
      { id: 'priority-distribution', visible: true, size: 'medium', order: 1 },
      { id: 'trend-chart', visible: true, size: 'wide', order: 2 },
      { id: 'customer-satisfaction', visible: true, size: 'medium', order: 3 },
      { id: 'resolution-rate', visible: true, size: 'medium', order: 4 },
      { id: 'top-categories', visible: true, size: 'medium', order: 5 },
      { id: 'top-analysts', visible: true, size: 'medium', order: 6 },
      { id: 'recent-tickets', visible: true, size: 'large', order: 7 },
      { id: 'sla-performance', visible: true, size: 'large', order: 8 },
    ]
  },
  manager: {
    name: 'Gerencial',
    description: 'Foco em métricas de gestão',
    cards: [
      { id: 'trend-chart', visible: true, size: 'extra-wide', order: 0 },
      { id: 'customer-satisfaction', visible: true, size: 'medium', order: 1 },
      { id: 'resolution-rate', visible: true, size: 'medium', order: 2 },
      { id: 'top-analysts', visible: true, size: 'wide', order: 3 },
      { id: 'sla-performance', visible: true, size: 'large', order: 4 },
    ]
  },
  analyst: {
    name: 'Analista',
    description: 'Foco em trabalho operacional',
    cards: [
      { id: 'recent-tickets', visible: true, size: 'extra-wide', order: 0 },
      { id: 'priority-distribution', visible: true, size: 'medium', order: 1 },
      { id: 'response-times', visible: true, size: 'medium', order: 2 },
      { id: 'sla-performance', visible: true, size: 'wide', order: 3 },
    ]
  }
} as const;

export type PresetKey = keyof typeof DASHBOARD_PRESETS;

// Interface para configuração exportável
export interface DashboardConfig {
  version: string;
  timestamp: string;
  preset?: PresetKey;
  cards: DashboardCard[];
  metadata?: {
    userName?: string;
    description?: string;
  };
}

const DEFAULT_CARDS: DashboardCard[] = [
  { id: 'debug', component: 'DebugMetrics', title: 'Debug - Status das Métricas', order: 0, visible: false, size: 'medium', colSpan: 1, rowSpan: 1 },
  { id: 'response-times', component: 'ResponseTimeMetrics', title: 'Tempos de Resposta', order: 1, visible: false, size: 'medium', colSpan: 1, rowSpan: 1 },
  { id: 'priority-distribution', component: 'PriorityDistribution', title: 'Distribuição por Prioridade', order: 2, visible: false, size: 'medium', colSpan: 1, rowSpan: 1 },
  { id: 'trend-chart', component: 'TrendChart', title: 'Tendências de Tickets', order: 3, visible: true, size: 'medium', colSpan: 1, rowSpan: 1 },
  { id: 'customer-satisfaction', component: 'CustomerSatisfaction', title: 'Satisfação do Cliente', order: 4, visible: false, size: 'medium', colSpan: 1, rowSpan: 1 },
  { id: 'resolution-rate', component: 'ResolutionRate', title: 'Taxa de Resolução', order: 5, visible: false, size: 'medium', colSpan: 1, rowSpan: 1 },
  { id: 'top-categories', component: 'TopCategories', title: 'Top 5 Categorias', order: 6, visible: true, size: 'medium', colSpan: 1, rowSpan: 1 },
  { id: 'top-analysts', component: 'TopAnalysts', title: 'Analistas Mais Produtivos', order: 7, visible: true, size: 'medium', colSpan: 1, rowSpan: 1 },
  { id: 'recent-tickets', component: 'RecentTickets', title: 'Tickets Recentes', order: 8, visible: true, size: 'large', colSpan: 1, rowSpan: 2 },
  { id: 'sla-performance', component: 'SLAPerformance', title: 'Performance SLA', order: 9, visible: true, size: 'large', colSpan: 1, rowSpan: 2 },
];

export const useDashboardLayout = () => {
  const [cards, setCards] = useState<DashboardCard[]>(DEFAULT_CARDS);
  const [isEditMode, setIsEditMode] = useState(false);
  const [lastAction, setLastAction] = useState<{ type: string; data?: any } | null>(null);

  // Carregar layout salvo do localStorage
  useEffect(() => {
    try {
      const savedLayout = localStorage.getItem('dashboard-layout');
      const savedVersion = localStorage.getItem('dashboard-config-version');
      
      // Se a versão mudou, forçar uso do layout padrão
      if (savedVersion !== CONFIG_VERSION) {
        console.log(`Versão de configuração atualizada de ${savedVersion} para ${CONFIG_VERSION}, aplicando configurações padrão`);
        setCards(DEFAULT_CARDS);
        localStorage.setItem('dashboard-config-version', CONFIG_VERSION);
        localStorage.setItem('dashboard-layout', JSON.stringify(DEFAULT_CARDS));
        return;
      }
      
      if (savedLayout) {
        const parsedLayout = JSON.parse(savedLayout);
        // Validar estrutura dos dados
        if (Array.isArray(parsedLayout) && parsedLayout.length > 0) {
          const isValidLayout = parsedLayout.every(card => 
            card && 
            typeof card.id === 'string' && 
            typeof card.title === 'string' && 
            typeof card.order === 'number' && 
            typeof card.visible === 'boolean'
          );
          
          if (isValidLayout) {
            // Verificar se os novos cards existem no layout salvo
            const hasNewCards = DEFAULT_CARDS.every(defaultCard => 
              parsedLayout.some(savedCard => savedCard.id === defaultCard.id)
            );
            
            if (hasNewCards) {
              setCards(parsedLayout);
            } else {
              // Se faltam cards novos, mesclar com o padrão
              const mergedCards = [...DEFAULT_CARDS];
              parsedLayout.forEach(savedCard => {
                const index = mergedCards.findIndex(card => card.id === savedCard.id);
                if (index !== -1) {
                  mergedCards[index] = savedCard;
                }
              });
              setCards(mergedCards);
              localStorage.setItem('dashboard-layout', JSON.stringify(mergedCards));
            }
          } else {
            console.warn('Layout salvo inválido, usando padrão');
            localStorage.removeItem('dashboard-layout');
            setCards(DEFAULT_CARDS);
          }
        }
      } else {
        // Primeira vez, salvar versão
        localStorage.setItem('dashboard-config-version', CONFIG_VERSION);
      }
    } catch (error) {
      console.error('Erro ao carregar layout do dashboard:', error);
      // Limpar dados corrompidos
      try {
        localStorage.removeItem('dashboard-layout');
        localStorage.removeItem('dashboard-config-version');
        setCards(DEFAULT_CARDS);
      } catch (e) {
        console.error('Erro ao limpar localStorage:', e);
      }
    }
  }, []);

  // Salvar layout no localStorage
  const saveLayout = useCallback((newCards: DashboardCard[]) => {
    try {
      localStorage.setItem('dashboard-layout', JSON.stringify(newCards));
      setCards(newCards);
    } catch (error) {
      console.error('Erro ao salvar layout:', error);
    }
  }, []);

  // Reordenar cards
  const reorderCards = useCallback((startIndex: number, endIndex: number) => {
    setCards(currentCards => {
      const result = Array.from(currentCards);
      const [removed] = result.splice(startIndex, 1);
      result.splice(endIndex, 0, removed);

      // Atualizar ordem
      const updatedCards = result.map((card, index) => ({
        ...card,
        order: index,
      }));

      // Salvar no localStorage
      try {
        localStorage.setItem('dashboard-layout', JSON.stringify(updatedCards));
      } catch (error) {
        console.error('Erro ao salvar reordenação:', error);
      }

      return updatedCards;
    });
  }, []);

  // Alternar visibilidade de um card
  const toggleCardVisibility = useCallback((cardId: string) => {
    setCards(currentCards => {
      // Verificar se o card existe
      const cardExists = currentCards.some(card => card.id === cardId);
      if (!cardExists) {
        console.error(`Card com ID "${cardId}" não encontrado`);
        return currentCards;
      }

      const updatedCards = currentCards.map(card =>
        card.id === cardId ? { ...card, visible: !card.visible } : card
      );
      
      // Verificar se não há duplicatas após a atualização
      const ids = updatedCards.map(card => card.id);
      const uniqueIds = [...new Set(ids)];
      
      if (ids.length !== uniqueIds.length) {
        console.error('Duplicatas detectadas após toggle de visibilidade:', ids);
        return currentCards; // Retornar estado anterior se houver problema
      }
      
      // Salvar no localStorage
      try {
        localStorage.setItem('dashboard-layout', JSON.stringify(updatedCards));
      } catch (error) {
        console.error('Erro ao salvar visibilidade:', error);
      }
      
      return updatedCards;
    });
  }, []);

  // Resetar para layout padrão
  const resetLayout = useCallback(() => {
    try {
      localStorage.removeItem('dashboard-layout');
      setCards(DEFAULT_CARDS);
    } catch (error) {
      console.error('Erro ao resetar layout:', error);
    }
  }, []);

  // Obter cards ordenados e visíveis
  const getVisibleCards = () => {
    const visibleCards = cards
      .filter(card => card.visible)
      .sort((a, b) => a.order - b.order);
    
    // Verificar duplicatas
    const ids = visibleCards.map(card => card.id);
    const uniqueIds = [...new Set(ids)];
    
    if (ids.length !== uniqueIds.length) {
      console.error('Cards visíveis com IDs duplicados detectados:', ids);
      // Remover duplicatas mantendo o primeiro
      const uniqueCards = visibleCards.filter((card, index, self) => 
        self.findIndex(c => c.id === card.id) === index
      );
      return uniqueCards;
    }
    
    return visibleCards;
  };

  // Obter todos os cards ordenados (para configuração)
  const getAllCards = () => {
    const allCards = cards.sort((a, b) => a.order - b.order);
    
    // Verificar duplicatas
    const ids = allCards.map(card => card.id);
    const uniqueIds = [...new Set(ids)];
    
    if (ids.length !== uniqueIds.length) {
      console.error('Todos os cards com IDs duplicados detectados:', ids);
      // Remover duplicatas mantendo o primeiro
      const uniqueCards = allCards.filter((card, index, self) => 
        self.findIndex(c => c.id === card.id) === index
      );
      return uniqueCards;
    }
    
    return allCards;
  };

  // Aplicar preset predefinido
  const applyPreset = useCallback((presetKey: PresetKey) => {
    try {
      const preset = DASHBOARD_PRESETS[presetKey];
      if (!preset) {
        throw new Error(`Preset "${presetKey}" não encontrado`);
      }

      // Criar nova configuração baseada no preset
      const newCards = DEFAULT_CARDS.map(defaultCard => {
        const presetCard = preset.cards.find(p => p.id === defaultCard.id);
        if (presetCard) {
          return {
            ...defaultCard,
            visible: presetCard.visible,
            size: presetCard.size as DashboardCard['size'],
            order: presetCard.order,
            colSpan: getColSpanFromSize(presetCard.size as DashboardCard['size']),
            rowSpan: getRowSpanFromSize(presetCard.size as DashboardCard['size']),
          };
        }
        return {
          ...defaultCard,
          visible: false, // Ocultar cards não incluídos no preset
        };
      });

      saveLayout(newCards);
      setLastAction({ type: 'preset-applied', data: { preset: presetKey, name: preset.name } });
      
      toast.success(`Preset "${preset.name}" aplicado!`, {
        description: preset.description,
        duration: 3000,
      });
      
    } catch (error) {
      console.error('Erro ao aplicar preset:', error);
      toast.error('Erro ao aplicar preset', {
        description: 'Não foi possível aplicar a configuração selecionada.',
      });
    }
  }, [saveLayout]);

  // Exportar configuração atual
  const exportConfig = useCallback((): DashboardConfig => {
    return {
      version: CONFIG_VERSION,
      timestamp: new Date().toISOString(),
      cards: getAllCards(),
      metadata: {
        description: 'Configuração exportada do SimpleDesk ITSM',
      },
    };
  }, []);

  // Importar configuração
  const importConfig = useCallback((config: DashboardConfig) => {
    try {
      // Validar estrutura da configuração
      if (!config || !config.cards || !Array.isArray(config.cards)) {
        throw new Error('Configuração inválida: estrutura incorreta');
      }

      // Validar versão (avisar se for diferente, mas não bloquear)
      if (config.version !== CONFIG_VERSION) {
        console.warn(`Versão da configuração (${config.version}) diferente da atual (${CONFIG_VERSION})`);
        toast.info('Versão diferente detectada', {
          description: 'A configuração pode não ser totalmente compatível.',
        });
      }

      // Validar e mesclar cards
      const importedCards = config.cards.filter(card => {
        // Verificar se o card existe nos padrões
        return DEFAULT_CARDS.some(defaultCard => defaultCard.id === card.id);
      });

      if (importedCards.length === 0) {
        throw new Error('Nenhum card válido encontrado na configuração');
      }

      // Mesclar com cards padrão para garantir que todos existam
      const mergedCards = DEFAULT_CARDS.map(defaultCard => {
        const importedCard = importedCards.find(card => card.id === defaultCard.id);
        return importedCard || defaultCard;
      });

      saveLayout(mergedCards);
      setLastAction({ type: 'config-imported', data: { timestamp: config.timestamp } });
      
      toast.success('Configuração importada!', {
        description: `${importedCards.length} cards configurados com sucesso.`,
        duration: 3000,
      });
      
    } catch (error) {
      console.error('Erro ao importar configuração:', error);
      toast.error('Erro ao importar configuração', {
        description: error instanceof Error ? error.message : 'Configuração inválida.',
      });
    }
  }, [saveLayout]);

  // Salvar configuração como arquivo
  const downloadConfig = useCallback((filename?: string) => {
    try {
      const config = exportConfig();
      const blob = new Blob([JSON.stringify(config, null, 2)], {
        type: 'application/json',
      });
      
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = filename || `dashboard-config-${new Date().toISOString().split('T')[0]}.json`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
      
      toast.success('Configuração baixada!', {
        description: 'Arquivo salvo com sucesso.',
      });
      
    } catch (error) {
      console.error('Erro ao baixar configuração:', error);
      toast.error('Erro ao baixar configuração');
    }
  }, [exportConfig]);

  // Função auxiliar para obter colSpan baseado no tamanho
  const getColSpanFromSize = (size: DashboardCard['size']): number => {
    switch (size) {
      case 'wide': return 2;
      case 'extra-wide': return 4;
      default: return 1;
    }
  };

  // Função auxiliar para obter rowSpan baseado no tamanho
  const getRowSpanFromSize = (size: DashboardCard['size']): number => {
    switch (size) {
      case 'large': return 2;
      case 'tall': return 3;
      default: return 1;
    }
  };

  return {
    cards: getVisibleCards(),
    allCards: getAllCards(),
    isEditMode,
    setIsEditMode,
    toggleCardVisibility,
    resetLayout,
    lastAction,
    // Novas funcionalidades
    applyPreset,
    exportConfig,
    importConfig,
    downloadConfig,
    presets: DASHBOARD_PRESETS,
  };
};
